import { FC, useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

import * as Styled from './PunchupAddOnsSection.styles';

import { AddonOrderItem, ApplicableAddonProps } from './types';
import { ButtonInfo } from '../PunchupTicketsSection/types';
import { Divider } from 'features/common/components/Divider';
import { PRIMARY, PUNCHUP_QUATERNARY } from 'features/common/components/Button/constants';
import { PunchupMultiButtonSectionTitle } from 'features/common/components/PunchupMultiButtonSectionTitle';
import { formatMoney } from 'utils/utils';
import { postToast } from 'features/common/utils/postFlash';
import { reorderAddonsPut } from './services';
import { useApplicableAddonsApi } from './hooks';
import { useAppSelector } from 'store/hooks';
import { useResponsiveScreen } from 'hooks/useWindowSize';
import { useTranslation } from 'react-i18next';

const sortByOrderIndex = (addons: ApplicableAddonProps[]) =>
	[...addons].sort((a, b) => {
		if (a.addon.orderIndex !== b.addon.orderIndex) return a.addon.orderIndex - b.addon.orderIndex;
		return a.addon.iD - b.addon.iD;
	});

const PunchupAddOnsSection: FC = () => {
	const { t } = useTranslation();
	const isLargeScreen = useResponsiveScreen();
	const { isPunchup } = useAppSelector((state) => state.auth);
	const { applicableAddons, refetch } = useApplicableAddonsApi();

	const [addons, setAddons] = useState<ApplicableAddonProps[]>([]);
	const [originalOrder, setOriginalOrder] = useState<AddonOrderItem[]>([]);
	const [showSaveButtons, setShowSaveButtons] = useState(false);

	useEffect(() => {
		if (applicableAddons) {
			const sorted = sortByOrderIndex(applicableAddons);
			setAddons(sorted);
			setOriginalOrder(sorted.map(({ addon }) => ({ iD: addon.iD, orderIndex: addon.orderIndex })));
			setShowSaveButtons(false);
		}
	}, [JSON.stringify(applicableAddons)]);

	const move = (index: number, direction: -1 | 1) => {
		const target = index + direction;
		if (target < 0 || target >= addons.length) return;
		const next = [...addons];
		[next[index], next[target]] = [next[target], next[index]];
		const reindexed = next.map((item, i) => ({ ...item, addon: { ...item.addon, orderIndex: i } }));
		setAddons(reindexed);
		setShowSaveButtons(true);
	};

	const onRevert = () => {
		const reverted = addons.map((item) => ({
			...item,
			addon: {
				...item.addon,
				orderIndex: originalOrder.find(({ iD }) => iD === item.addon.iD)?.orderIndex ?? item.addon.orderIndex,
			},
		}));
		setAddons(sortByOrderIndex(reverted));
		setShowSaveButtons(false);
	};

	const onSave = async () => {
		try {
			await reorderAddonsPut(addons.map(({ addon }) => ({ iD: addon.iD, orderIndex: addon.orderIndex })));
			setOriginalOrder(addons.map(({ addon }) => ({ iD: addon.iD, orderIndex: addon.orderIndex })));
			setShowSaveButtons(false);
			postToast('success', t('dashboard-addOns-reorder-success'));
			refetch();
		} catch (error) {
			postToast('error', t('dashboard-addOns-reorder-error'));
		}
	};

	const saveButtons: ButtonInfo[] = [
		{ label: t('dashboard-addOns-reorder-revert'), onClick: onRevert },
		{
			label: t('dashboard-addOns-reorder-save'),
			onClick: onSave,
			category: isPunchup ? PUNCHUP_QUATERNARY : PRIMARY,
		},
	];

	const title = t('dashboard-addOns') + (addons.length > 0 ? ` (${addons.length})` : '');

	return (
		<>
			<PunchupMultiButtonSectionTitle
				title={title}
				buttons={showSaveButtons ? saveButtons : []}
				dataCy="addons-section"
			/>

			<Styled.AddOnsContainer data-cy="addons-list">
				{addons.length > 0 ? (
					addons.map(({ addon }, index) => (
						<Styled.AddonRow key={addon.iD}>
							<Styled.PositionBadge>{index + 1}</Styled.PositionBadge>
							{addon.image ? <Styled.Thumbnail src={addon.image} alt={addon.name} /> : <Styled.ThumbnailPlaceholder />}
							<Styled.AddonInfo>
								<Styled.AddonName>{addon.name}</Styled.AddonName>
								<Styled.AddonPrice>${formatMoney(addon.defaultPrice)}</Styled.AddonPrice>
							</Styled.AddonInfo>
							<Styled.ArrowButtons>
								<Styled.ArrowButton
									onClick={() => move(index, -1)}
									disabled={index === 0}
									aria-label={t('dashboard-addOns-move-up')}
									data-cy="addon-move-up"
								>
									<ChevronUp size={16} />
								</Styled.ArrowButton>
								<Styled.ArrowButton
									onClick={() => move(index, 1)}
									disabled={index === addons.length - 1}
									aria-label={t('dashboard-addOns-move-down')}
									data-cy="addon-move-down"
								>
									<ChevronDown size={16} />
								</Styled.ArrowButton>
							</Styled.ArrowButtons>
						</Styled.AddonRow>
					))
				) : (
					<Styled.EmptyState>{t('dashboard-addOns-empty')}</Styled.EmptyState>
				)}
			</Styled.AddOnsContainer>

			{!isLargeScreen && <Divider />}
		</>
	);
};

export { PunchupAddOnsSection };
